import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Spinner, Form, Row, Col } from 'react-bootstrap';
import { Check, X, Edit2, Save, SkipForward } from 'lucide-react';
import { useHistory } from 'react-router-dom';
import Dollars from '../../common/typeFormatting/Dollars';
import { getAPAccounts } from '../VoucherGroups/api/VoucherGroupApi';
import { getExpenseEligibleEmployees, applyOneTimeExpense } from './api/OneTimeExpenseApi';

const today = () => new Date().toISOString().split('T')[0];

const BulkOneTimeExpense = () => {
    const history = useHistory();

    const [apAccounts, setApAccounts] = useState([]);
    const [apAccountId, setApAccountId] = useState('');
    const [employees, setEmployees] = useState([]);
    const [selectedEmployees, setSelectedEmployees] = useState(new Set());

    const [defaultAmount, setDefaultAmount] = useState('');
    const [expenseDate, setExpenseDate] = useState(today());
    const [description, setDescription] = useState('');

    const [editingRow, setEditingRow] = useState(null);
    const [editedValues, setEditedValues] = useState({});
    const [processing, setProcessing] = useState({}); // { [employeeId]: 'processing' | 'success' | 'skipped' | 'error' }

    const [loadingAccounts, setLoadingAccounts] = useState(true);
    const [loadingEmployees, setLoadingEmployees] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchAccounts = async () => {
            try {
                const accounts = await getAPAccounts();
                setApAccounts(accounts);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoadingAccounts(false);
            }
        };
        fetchAccounts();
    }, []);

    const handleApAccountChange = async (e) => {
        const value = e.target.value;
        setApAccountId(value);
        setEmployees([]);
        setSelectedEmployees(new Set());
        setProcessing({});
        setEditedValues({});
        setEditingRow(null);

        if (!value) return;

        setLoadingEmployees(true);
        try {
            const data = await getExpenseEligibleEmployees(value);
            const withAmounts = data.map(emp => ({
                ...emp,
                amount: defaultAmount === '' ? 0 : parseFloat(defaultAmount)
            }));
            setEmployees(withAmounts);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoadingEmployees(false);
        }
    };

    // Changing the default amount applies it to every row (per-row edits can still override after).
    const handleDefaultAmountChange = (value) => {
        setDefaultAmount(value);
        const amount = value === '' ? 0 : parseFloat(value);
        setEmployees(prev => prev.map(emp => ({ ...emp, amount })));
    };

    const handleSelectionChange = (employeeId) => {
        setSelectedEmployees(prev => {
            const next = new Set(prev);
            if (next.has(employeeId)) next.delete(employeeId);
            else next.add(employeeId);
            return next;
        });
    };

    const handleSelectAll = () => {
        const allSelected = employees.length === selectedEmployees.size;
        setSelectedEmployees(allSelected ? new Set() : new Set(employees.map(e => e.employeeId)));
    };

    const handleRowClick = (employeeId) => {
        setEditingRow(employeeId);
        const emp = employees.find(e => e.employeeId === employeeId);
        setEditedValues(prev => ({ ...prev, [employeeId]: { amount: emp.amount } }));
    };

    const handleEditChange = (employeeId, value) => {
        setEditedValues(prev => ({ ...prev, [employeeId]: { amount: value } }));
    };

    const handleSaveRow = (employeeId) => {
        setEmployees(prev => prev.map(emp =>
            emp.employeeId === employeeId
                ? { ...emp, amount: parseFloat(editedValues[employeeId].amount) }
                : emp
        ));
        setEditingRow(null);
        setEditedValues(prev => {
            const next = { ...prev };
            delete next[employeeId];
            return next;
        });
    };

    const applyExpenses = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            const selected = employees.filter(e => selectedEmployees.has(e.employeeId));

            for (const emp of selected) {
                setProcessing(prev => ({ ...prev, [emp.employeeId]: 'processing' }));
                try {
                    // One call per employee mirrors the Bulk Issue page and gives per-row feedback.
                    const result = await applyOneTimeExpense({
                        apAccountId: parseInt(apAccountId, 10),
                        expenseDate,
                        description,
                        entries: [{ employeeId: emp.employeeId, amount: emp.amount }]
                    });

                    const skipped = (result.skippedEmployeeIds || []).includes(emp.employeeId);
                    const succeeded = !skipped && result.processedCount > 0;
                    setProcessing(prev => ({
                        ...prev,
                        [emp.employeeId]: skipped ? 'skipped' : (succeeded ? 'success' : 'error')
                    }));

                    if (succeeded) {
                        // Reflect the charge in the displayed Current Balance (an expense of X
                        // reduces the AP-account balance by exactly X, even when it goes negative).
                        setEmployees(prev => prev.map(e =>
                            e.employeeId === emp.employeeId
                                ? { ...e, currentBalance: e.currentBalance - emp.amount }
                                : e
                        ));
                        // Uncheck the row so it can't be applied again (the checkmark remains).
                        setSelectedEmployees(prev => {
                            const next = new Set(prev);
                            next.delete(emp.employeeId);
                            return next;
                        });
                    }
                } catch (err) {
                    console.error('Error applying expense:', err);
                    setProcessing(prev => ({ ...prev, [emp.employeeId]: 'error' }));
                }
            }
        } finally {
            // Always re-enable the button, even if the loop throws unexpectedly.
            setSubmitting(false);
        }
    };

    const renderStatusCell = (employee) => {
        const status = processing[employee.employeeId];
        // Keep the checkbox always available (so additional expenses can be applied),
        // and show the most recent per-row result beside it.
        return (
            <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                <input
                    type="checkbox"
                    checked={selectedEmployees.has(employee.employeeId)}
                    onChange={() => handleSelectionChange(employee.employeeId)}
                    disabled={submitting}
                />
                {status === 'processing' && <Spinner size="sm" animation="border" />}
                {status === 'success' && <Check className="text-success" size={18} />}
                {status === 'skipped' && <SkipForward className="text-warning" size={18} title="No voucher on this AP account" />}
                {status === 'error' && <X className="text-danger" size={18} />}
            </div>
        );
    };

    if (loadingAccounts) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;

    const totalSelected = selectedEmployees.size;
    const totalAmount = employees
        .filter(e => selectedEmployees.has(e.employeeId))
        .reduce((sum, e) => sum + (e.amount || 0), 0);

    return (
        <div className="p-4">
            <Card className="mb-4">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <Card.Title>Bulk One-Time Expense</Card.Title>
                        <Button variant="secondary" onClick={() => history.push('/travel/vouchergroups')}>
                            Back
                        </Button>
                    </div>

                    <Row className="mb-3">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>AP Account (Allowance Type)</Form.Label>
                                <Form.Control as="select" value={apAccountId} onChange={handleApAccountChange}>
                                    <option value="">Select an AP account...</option>
                                    {apAccounts.map(acct => (
                                        <option key={acct.apAccountId} value={acct.apAccountId}>
                                            {acct.accountNumber} - {acct.description}
                                        </option>
                                    ))}
                                </Form.Control>
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>Default Amount</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    value={defaultAmount}
                                    onChange={(e) => handleDefaultAmountChange(e.target.value)}
                                    placeholder="Applies to all rows"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>Expense Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={expenseDate}
                                    onChange={(e) => setExpenseDate(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Description</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="e.g. Team offsite"
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    {apAccountId && (
                        <>
                            <div className="mb-3 p-3 bg-light">
                                <div className="row">
                                    <div className="col-md-6"><strong>Selected:</strong> {totalSelected}</div>
                                    <div className="col-md-6"><strong>Total Amount:</strong> <Dollars>{totalAmount}</Dollars></div>
                                </div>
                            </div>

                            <div className="d-flex justify-content-between mb-3">
                                <Button variant="secondary" onClick={handleSelectAll} disabled={employees.length === 0}>
                                    {employees.length > 0 && employees.length === selectedEmployees.size ? 'Unselect All' : 'Select All'}
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={applyExpenses}
                                    disabled={selectedEmployees.size === 0 || !description || submitting}
                                >
                                    {submitting ? 'Applying...' : 'Apply Expense to Selected'}
                                </Button>
                            </div>

                            {loadingEmployees ? (
                                <div>Loading employees...</div>
                            ) : (
                                <Table hover>
                                    <thead>
                                        <tr>
                                            <th>Select/Status</th>
                                            <th>Employee Name</th>
                                            <th>Current Balance</th>
                                            <th>Amount</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {employees.map(employee => (
                                            <tr key={employee.employeeId}>
                                                <td>{renderStatusCell(employee)}</td>
                                                <td>{`${employee.lastName}, ${employee.firstName}`}</td>
                                                <td><Dollars>{employee.currentBalance}</Dollars></td>
                                                <td>
                                                    {editingRow === employee.employeeId ? (
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            className="form-control"
                                                            value={editedValues[employee.employeeId]?.amount}
                                                            onChange={(e) => handleEditChange(employee.employeeId, e.target.value)}
                                                        />
                                                    ) : (
                                                        <Dollars>{employee.amount}</Dollars>
                                                    )}
                                                </td>
                                                <td>
                                                    {editingRow === employee.employeeId ? (
                                                        <Button variant="link" onClick={() => handleSaveRow(employee.employeeId)}>
                                                            <Save size={16} />
                                                        </Button>
                                                    ) : (
                                                        <Button variant="link" onClick={() => handleRowClick(employee.employeeId)}>
                                                            <Edit2 size={16} />
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {employees.length === 0 && (
                                            <tr><td colSpan={5} className="text-center text-muted">No eligible employees for this AP account.</td></tr>
                                        )}
                                    </tbody>
                                </Table>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default BulkOneTimeExpense;
